import { configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import authReducer from "./features/auth/authSlice";
import { createReducerManager, ReducerManager } from "./reducerManager";

// Define static reducers that should always be loaded
const staticReducers = {
	auth: authReducer,
};

// Create the reducer manager
const reducerManager = createReducerManager(staticReducers);

// Create store with reducer manager
const store = configureStore({
	reducer: reducerManager.reduce,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				// Ignore these action types
				ignoredActions: [
					"auth/initializeAuth/fulfilled",
					"admin/howto/createHowTo/pending",
					"admin/howto/createHowTo/fulfilled",
					"admin/howto/createHowTo/rejected",
					"admin/howto/updateHowTo/pending",
					"admin/howto/updateHowTo/fulfilled",
					"admin/howto/updateHowTo/rejected",
				],
				// Ignore these field paths in all actions
				ignoredActionPaths: ["payload.account.tenantProfiles", "meta.arg.data"],
				// Ignore these paths in the state
				ignoredPaths: ["auth.user.tenantProfiles"],
			},
		}),
});

// Add the reducer manager to the store
// biome-ignore lint/suspicious/noExplicitAny: Any is required for the reducer manager
(store as any).reducerManager = reducerManager;

// Export store types
export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;

// Create a type that includes both static and dynamic reducers
export interface DynamicAppStore extends AppStore {
	reducerManager: ReducerManager;
}

// Import dynamic reducer types
import type { DurationBenchmarkState } from "./features/abacus/durationBenchmarkTypes";
import type { ProductivityState } from "./features/abacus/productivity/productivitySlice";
import type { HowToState as AdminHowToState } from "./features/admin/howtoSlice";
import type { BenchmarkState } from "./features/galileo/benchmarkSlice";
import type { SearchState } from "./features/galileo/searchSlice";
import type { HowToState } from "./features/howto/howtoSlice";
import type { MapFilterState } from "./features/map/mapFilterSlice";
import type { MapState } from "./features/map/mapSlice";

// Type for the root state with dynamic reducers
export interface RootState extends ReturnType<typeof store.getState> {
	durationBenchmark?: DurationBenchmarkState;
	productivity?: ProductivityState;
	methodologies?: MethodologiesState;
	benchmark?: BenchmarkState;
	search?: SearchState;
	map?: MapState;
	mapFilter?: MapFilterState;
	adminHowTo?: AdminHowToState;
	howto?: HowToState;
}

// Typed hooks
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();

export { reducerManager };
export default store as DynamicAppStore;
